import type {
  IFormStatus,
  IPutApplicationFormStatusNotificationPayload,
  IStatusNotification,
  TNotificationChannel,
  TNotificationMode,
} from '@/api/Rover/ApplicationFormNotifications/types';
import type {
  Channel,
  ChannelContent,
  ChannelEnabled,
  MessageOption,
  MessageSource,
  StatusNotificationConfig,
  StatusNotificationSummary,
} from '@/api/Rover/notifications/roverNotifications.types';
import { CHANNELS } from '@/api/Rover/notifications/roverNotifications.types';
import { hasCustomContent } from '@/features/ApplicationForm/utils/notifications/renderMessage';
import { defaultContentForStatusLabel } from './staticDefaults';

const API_CHANNEL: Record<Channel, TNotificationChannel> = {
  email: 'EMAIL',
  sms: 'SMS',
  onscreen: 'ON_SCREEN',
};

function channelEnabled(notification: IStatusNotification | null | undefined): ChannelEnabled {
  const base: ChannelEnabled = { email: true, sms: true, onscreen: true };
  if (!notification?.channels?.length) return base;
  return {
    email: notification.channels.find((c) => c.channel === 'EMAIL')?.enabled ?? true,
    sms: notification.channels.find((c) => c.channel === 'SMS')?.enabled ?? true,
    onscreen: notification.channels.find((c) => c.channel === 'ON_SCREEN')?.enabled ?? true,
  };
}

function toContent(notification: IStatusNotification | null | undefined, statusLabel: string): ChannelContent {
  const defaults = defaultContentForStatusLabel(statusLabel);
  if (!notification) {
    return {
      email: { subject: null, body: null },
      sms: { body: null },
      onscreen: { title: null, body: null },
    };
  }
  return {
    email: {
      subject: notification.emailSubject,
      body: notification.emailBody,
    },
    sms: { body: notification.smsBody },
    onscreen: {
      title: notification.onScreenBody ? defaults.onscreen.title : null,
      body: notification.onScreenBody,
    },
  };
}

function deriveSource(mode: TNotificationMode | undefined, optionCount: number, content: ChannelContent): MessageSource {
  if (mode === 'OPTIONS' || optionCount > 0) return 'options';
  if (mode === 'CUSTOM' || CHANNELS.some((ch) => hasCustomContent(ch, content))) return 'custom';
  return 'default';
}

export function mapFormStatusToSummary(row: IFormStatus): StatusNotificationSummary {
  const content = toContent(row.notification, row.name);
  const options = row.notification?.options ?? [];
  const activeOptionCount = options.filter((o) => o.active).length;
  return {
    status: row.id,
    statusLabel: row.name,
    source: row.notification ? deriveSource(row.notification.mode, activeOptionCount, content) : 'default',
    enabled: channelEnabled(row.notification),
    activeOptionCount,
  };
}

export function mapFormStatusToConfig(row: IFormStatus): StatusNotificationConfig {
  const summary = mapFormStatusToSummary(row);
  return {
    ...summary,
    content: toContent(row.notification, row.name),
    defaults: defaultContentForStatusLabel(row.name),
    optionsSelectionMode: 'multiple',
    optionsFormat: 'bullets',
    updatedAt: row.notification?.updatedAt ?? null,
    updatedBy: null,
  };
}

export function mapNotificationOptions(notification: IStatusNotification | null | undefined): MessageOption[] {
  return [...(notification?.options ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((o) => ({
      id: o.id,
      label: o.label,
      messageText: o.messageText,
      isActive: o.active,
      sortOrder: o.position,
    }));
}

export function toSavePayload(
  draft: { content: ChannelContent; enabled: ChannelEnabled },
  options: MessageOption[]
): IPutApplicationFormStatusNotificationPayload {
  const activeOptions = options.filter((o) => o.isActive);
  const hasCustom = CHANNELS.some((ch) => hasCustomContent(ch, draft.content));
  let mode: TNotificationMode = 'DEFAULT';
  if (activeOptions.length > 0) mode = 'OPTIONS';
  else if (hasCustom) mode = 'CUSTOM';

  const onScreenParts = [draft.content.onscreen.title, draft.content.onscreen.body].filter(Boolean);
  return {
    mode,
    emailSubject: draft.content.email.subject,
    emailBody: draft.content.email.body,
    smsBody: draft.content.sms.body,
    onScreenBody: onScreenParts.length > 0 ? onScreenParts.join('\n\n') : null,
    channels: CHANNELS.map((ch) => ({ channel: API_CHANNEL[ch], enabled: draft.enabled[ch] })),
    options: options.map((o, index) => ({
      label: o.label,
      messageText: o.messageText,
      position: index,
      active: o.isActive,
    })),
  };
}

export function mapSavedNotificationToConfig(row: IFormStatus, saved: IStatusNotification): StatusNotificationConfig {
  return mapFormStatusToConfig({ ...row, notification: saved });
}
